import { useState } from 'react';
import { rotateProjectSuffix } from '@/lib/api';
import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { Button } from '../ui/Button';
import { AlertTriangle, X } from 'lucide-react';

interface ChangeSuffixModalProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

export const ChangeSuffixModal = ({ projectId, projectName, onClose }: ChangeSuffixModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const refreshProjects = useProjectStore((state) => state.fetchProjects);
  const { addToast } = useToastStore.getState();

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await rotateProjectSuffix(projectId);
      await refreshProjects();
      addToast({
        type: 'success',
        message: `Suffix updated to ${response.suffix}. Re-export the project before importing into Luma.`
      });
      onClose();
    } catch (error) {
      console.error('Failed to rotate suffix:', error);
      addToast({ type: 'error', message: 'Failed to change suffix. Please try again.' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold">Change Project Suffix</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground" 
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 text-sm">
          <p>
            This will generate a new unique suffix for <strong>{projectName}</strong>. Any previously exported bundles
            will be out of date, and you&apos;ll need to re-export before importing into Luma.
          </p>
          <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-md p-3">
            <p className="text-xs text-orange-900 dark:text-orange-100">
              ⚠️ This action cannot be undone. Existing identifiers will be updated to use the new suffix.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="border-amber-500 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/20"
          >
            {isSubmitting ? 'Generating…' : 'Generate New Suffix'}
          </Button>
        </div>
      </div>
    </div>
  );
};
